import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { Body } from '../typography/Body';

export type CompactTimelineItem = { title: string; body: string };

export type CompactTimelineProps = {
  items: CompactTimelineItem[];
};

const markerColumnWidth = '14px';
const dotSize = '11px';
const dotTopOffset = '7px';

const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li`
  display: flex;
  gap: ${space.sm};
  margin: 0;
  padding: 0;
`;

const MarkerColumn = styled.div`
  position: relative;
  flex: 0 0 ${markerColumnWidth};
  width: ${markerColumnWidth};
  display: flex;
  justify-content: center;
`;

const Dot = styled.span`
  flex-shrink: 0;
  width: ${dotSize};
  height: ${dotSize};
  margin-top: ${dotTopOffset};
  border-radius: ${radii.pill};
  background: ${colors.primary};
`;

const Connector = styled.span`
  position: absolute;
  top: calc(${dotTopOffset} + ${dotSize});
  bottom: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: ${getMediumRGBA(0.3)};
`;

const TextColumn = styled.div<{ $isLast: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: ${({ $isLast }) => ($isLast ? 0 : space.sm)};
`;

const Title = styled.p`
  margin: 0;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 500;
  color: ${colors.dark};
`;

/** Inline "Ablauf" timeline for the expanded 09 detail (dot + connector, not `ListTimeline`'s numbered circles). */
export function CompactTimeline({ items }: CompactTimelineProps) {
  const lastIndex = items.length - 1;

  return (
    <List>
      {items.map((item, index) => {
        const isLast = index === lastIndex;
        return (
          <Row key={index}>
            <MarkerColumn aria-hidden>
              <Dot />
              {isLast ? null : <Connector />}
            </MarkerColumn>
            <TextColumn $isLast={isLast}>
              <Title>{item.title}</Title>
              <Body variant="document" tone="muted">
                {item.body}
              </Body>
            </TextColumn>
          </Row>
        );
      })}
    </List>
  );
}
